import {useHistory, useParams} from "react-router-dom";
import {useContext, useEffect, useRef} from "react";
import {useState} from "react";
import {useQueryClient} from "react-query";
import { useTranslation } from "react-i18next";
import { fetchOrderReassingCarrierBulkTracker,  OrderReassingCarrierBulk} from "../../../services/orderServices";
import { lookupUberBatch } from "../../../services/uberBatchesService";
import { TrashIcon } from "../../../components/Icons/TrashIcon"
import { CollapseContainer, InputField, SearchInput, NoticeBanner, Notification } from '../../../components'
import {
    ActionMenu,
    ConfirmDialog,
    DialogView,
    FormattedDate,
    NewTable,
    PageTopBar,
    PageView,
    SlidePanel,
    Button,
    Loader
  } from "../../../components";
import { navigation } from "../../../navigation/NavegationRoutes";
import { UserContext } from "../../../hooks/UserContext";
import { UberBatchesList } from "../../UberBatches";

const MAX_LABELS = 50;

export const ReassingCarrier = ({}) => {
    const { i18n } = useTranslation();
    const { user } = useContext(UserContext);
    const { warehouse_id } = useParams();
    const showUberBatches = !!user?.permissions?.index_uber_batches
        && !!user?.current_warehouse?.uber_mcb_enabled;

    const [isLoading, setIsLoading] = useState(false);
    const [labels, setLabels] = useState([]);
    const [openError, setOpenError] = useState({status: false, text: ''});
    const [showLabelScanned, setShowLabelScanned] = useState(true);
    const [isComplete, setIsComplete] = useState(false)
    const [failures, setFailures] = useState(0)
    const [total, setTotal] = useState(0)
    const [pending, setPending] = useState(0)
    const [failuresInfo, setFailuresInfo] = useState([])
    const [perLabelFailures, setPerLabelFailures] = useState([])
    const [successCount, setSuccessCount] = useState(null)
    const [uberBatchesResult, setUberBatchesResult] = useState([])
    const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
    const [confirmLoading, setConfirmLoading] = useState(false);
    const [showFailures, setShowFailures] = useState(false);
    const [notification, setNotification] = useState(null);
    const [batchLabel, setBatchLabel] = useState('');
    const [labelLookup, setLabelLookup] = useState({state: 'idle'});

    const searchinputRef = useRef()

    useEffect(() => {
        searchinputRef.current?.focus()
    }, [])

    useEffect(() => {
        if (!batchLabel) {
            setLabelLookup({state: 'idle'})
            return
        }
        const timeout = setTimeout(async () => {
            setLabelLookup({state: 'loading'})
            try {
                const data = await lookupUberBatch(parseInt(warehouse_id, 10), batchLabel)
                if (data.exists === false) {
                    setLabelLookup({state: 'available', data})
                } else if (data.status === 'pending') {
                    setLabelLookup({state: 'pending', data})
                } else {
                    setLabelLookup({state: 'submitted', data})
                }
            } catch (e) {
                setLabelLookup({state: 'error', data: e?.response?.data})
            }
        }, 400)
        return () => clearTimeout(timeout)
    }, [batchLabel, warehouse_id])

    const handleSearchChange = async (searchString) => {
        if (searchString === '') return
        if (labels.length >= MAX_LABELS) {
            setNotification({
                type: 'error',
                title: i18n.t('reassign_carrier.scanner.limit_reached_title', { max: MAX_LABELS }),
                description: i18n.t('reassign_carrier.scanner.limit_reached_description', { max: MAX_LABELS })
            })
            searchinputRef.current?.resetInput()
            return
        }
        if (labels.includes(searchString)) {
            setNotification({
                type: 'error',
                title: i18n.t('reassign_carrier.scanner.duplicate_title'),
                description: i18n.t('reassign_carrier.scanner.duplicate_description', { label: searchString })
            })
            searchinputRef.current?.resetInput()
            return
        }
        setLabels((prevLabels) => [...prevLabels, searchString]);
        searchinputRef.current?.resetInput()
    }

    const removeLabel = (labelToRemove) => {
        setLabels((prevLabels) => prevLabels.filter(label => label !== labelToRemove));
    };

    const handleOpenConfirmDialog = () => {
        setOpenConfirmDialog(true)
    }

    const handleRetryFailedLabels = () => {
        const failedShippingNumbers = perLabelFailures.map((f) => f.shipping_number)
        setLabels(failedShippingNumbers)

        // Reset label state first; then prefill if a prior batch is known
        setBatchLabel('')
        setLabelLookup({state: 'idle'})
        const sourceBatch = uberBatchesResult?.[0]
        if (sourceBatch?.internal_label) {
            setBatchLabel(sourceBatch.internal_label)
        }
        setIsComplete(false)
        setFailures(0)
        setSuccessCount(null)
        setFailuresInfo([])
        setPerLabelFailures([])
        setUberBatchesResult([])
        setTotal(0)
        setPending(0)
        setShowFailures(false)
    }

    const handleConfirmRellocation = async () => {
        try {
            setConfirmLoading(true)
            let batch = await OrderReassingCarrierBulk({
                "scanned_labels": labels,
                "carrier_name": "UBER",
                "delivery_type": "SameDay",
                "warehouse_id": parseInt(warehouse_id, 10),
                ...(batchLabel ? { "internal_label": batchLabel } : {})
            })
            watchReallocaionProgress(batch.batch_id)
        } catch(e) {
            setConfirmLoading(false)
            setOpenConfirmDialog(false)

            const responseData = e?.response?.data
            if (responseData) {
                const parts = []
                if (responseData.cross_warehouse_labels?.length > 0) {
                    const items = responseData.cross_warehouse_labels
                        .map(l => `${l.shipping_number} (almacén ${l.actual_warehouse_id})`)
                        .join(', ')
                    parts.push(i18n.t('reassign_carrier.error_cross_warehouse_labels', { labels: items }))
                }
                if (responseData.not_found_labels?.length > 0) {
                    parts.push(i18n.t('reassign_carrier.error_not_found_labels', { labels: responseData.not_found_labels.join(', ') }))
                }
                const description = parts.length > 0
                    ? parts.join('. ')
                    : (responseData.error || i18n.t('reassign_carrier.bulk_error_description'))
                setNotification({
                    type: 'error',
                    title: i18n.t('reassign_carrier.bulk_error_title'),
                    description
                })
            } else {
                setNotification({
                    type: 'error',
                    title: i18n.t('reassign_carrier.bulk_error_title'),
                    description: i18n.t('reassign_carrier.bulk_error_description')
                })
            }
        }
    }

    const watchReallocaionProgress = function (bulkId) {
        if (isComplete || failures || total || pending)
            return true

        const intervalId = setInterval(async () => {
            try {
                const {
                    is_complete,
                    failures: _failures,
                    failure_count: _failure_count,
                    total: _total,
                    pending: _pending,
                    fail_info: _fail_info,
                    success_count: _success_count,
                    uber_batches: _uber_batches,
                } = await fetchOrderReassingCarrierBulkTracker(bulkId)

                // Detect outcomes shape vs Sidekiq-only shape
                const hasOutcomesShape = Array.isArray(_failures)
                const sidekiqFailures = hasOutcomesShape ? (_failure_count ?? 0) : (_failures ?? 0)

                setIsComplete(is_complete)
                setFailures(sidekiqFailures)
                setFailuresInfo(_fail_info ?? [])
                setTotal(_total)
                setPending(_pending)

                if (hasOutcomesShape) {
                    setPerLabelFailures(_failures)
                }
                if (_success_count != null) setSuccessCount(_success_count)
                if (_uber_batches?.length > 0) setUberBatchesResult(_uber_batches)

                if (isLoading)
                    setIsLoading(false)

                if (is_complete) {
                    setConfirmLoading(false)
                    setOpenConfirmDialog(false)
                    if (hasOutcomesShape ? _failures.length > 0 : (sidekiqFailures > 0 && (_fail_info ?? []).length > 0)) {
                        setShowFailures(true)
                    }
                    clearInterval(intervalId);
                }

            } catch (e) {
                clearInterval(intervalId);
                console.log(e)
            }
        }, 1000)
      }
    return (
       <>
       <ConfirmDialog
                        open={openConfirmDialog}
                        setOpen={setOpenConfirmDialog}
                        title={"Reasignar guías a Uber"}
                        description={ `¿Deseas reasignar ${labels.length} guías a Uber?`}
                        confirmLabel="Si"
                        onConfirm={() => {
                            handleConfirmRellocation()
                        }}
                        loading={confirmLoading}
                    />
       <PageView
        topMenu={<PageTopBar>
            <div className="text-lg font-semibold">Reasignación de paqueteria Uber </div>

            { isComplete &&
                <Button className="whitespace-nowrap mr-2" onClick={() =>{window.location.reload()}} >
                    Nueva Reasignación
                </Button>
            }

      </PageTopBar>}
      >
        {(isComplete ) ? (
            <>

                        <section>
                            <CollapseContainer
                                options={{ collapse: false }}
                                headerComponent={
                                    <h2 className="mb-0 text-2xl font-semi-bold">{'Reasignación Finalizada'}{showLabelScanned}</h2>
                                }

                            >
                            <div className="flex-full">
                                <br></br>
                                Se asignaron {successCount != null ? successCount : (total - failures)} paquetes a Uber
                                <br></br>
                                {failures > 0 && <p className='text-red-500'>No se pudieron reasignar <b>{failures} paquetes</b>  </p>}
                                {uberBatchesResult.length > 0 && (
                                    <p className="text-gray-700 mt-1">
                                        {i18n.t('reassign_carrier.created_batches_label')}{' '}
                                        {uberBatchesResult.map((b, idx) => (
                                            <span key={b.id}>
                                                {idx > 0 && ', '}
                                                <span className="font-mono">{b.internal_label}</span>
                                                {' '}({b.deliveries_count} entregas)
                                            </span>
                                        ))}
                                    </p>
                                )}
                            </div>
                           <div className="flex-full">
                                <br></br>
                                {/* Per-label outcomes shape (CUBBO-2046) */}
                                {perLabelFailures.length > 0 && (
                                    <>
                                        <h3 className="font-semibold text-red-700 mb-2">
                                            {i18n.t('reassign_carrier.failed_labels_header')} ({perLabelFailures.length})
                                        </h3>
                                        <table className="p-2 w-full text-left">
                                            <thead>
                                                <tr>
                                                    <th className="p-2 border-b">{i18n.t('reassign_carrier.shipping_number_column')}</th>
                                                    <th className="p-2 border-b">{i18n.t('reassign_carrier.error_column')}</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {perLabelFailures.map((f) => (
                                                    <tr key={f.shipping_number}>
                                                        <td className="text-left p-2 whitespace-nowrap border-t font-mono text-sm">
                                                            {f.shipping_number}
                                                        </td>
                                                        <td className="text-left p-2 border-t">
                                                            <span className="text-red-800 px-2.5 py-0.5">
                                                                {f.error_message}
                                                            </span>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                        <div className="pt-4">
                                            <Button
                                                className="whitespace-nowrap"
                                                onClick={handleRetryFailedLabels}
                                            >
                                                {i18n.t('reassign_carrier.retry_failed_labels')}
                                            </Button>
                                        </div>
                                    </>
                                )}
                                {/* Sidekiq-only fallback shape */}
                                {perLabelFailures.length === 0 && failuresInfo.length > 0 &&
                                    <table className="p-2 w-full text-left">
                                        <thead>
                                            <tr>
                                                <th>{"N° de guia -> Error"}</th>
                                            </tr>
                                        </thead>

                                        <tbody>
                                            {failuresInfo.map((failure_info) => {
                                                return (
                                                    <tr>
                                                        <td className="text-left p-2 whitespace-nowrap border-t">
                                                            <span className='text-red-800 px-2.5 py-0.5'>
                                                                {failure_info.error_message}
                                                            </span>
                                                        </td>
                                                    </tr>
                                            )})
                                            }
                                        </tbody>
                                    </table>
                                }
                            </div>

                            </CollapseContainer>
                        </section>
            </>
        ) : (
            <>
            <section>
                <CollapseContainer
                    options={{ collapse: !showLabelScanned }}
                    headerComponent={
                        showLabelScanned ? <h2 className="mb-0 text-2xl font-semi-bold">{'Escanea las guias de paquetes reasignados a Cuboo que deseas reasignar a Uber'} {showLabelScanned}</h2> : <h2 className="mb-0 text-2xl font-semi-bold">{`Escaneaste ${labels.length} paquetes para reasignar a Uber`} {showLabelScanned}</h2>
                    }
                    footerComponent={
                        showLabelScanned ?
                        <>
                            <div className="pt-5 pb-5 text-right">
                                <Button className="whitespace-nowrap mr-2" onClick={handleOpenConfirmDialog} disabled={labels.length == 0 || labelLookup.state === 'submitted' || labelLookup.state === 'loading'} >
                                    Reasignar a Uber
                                </Button>
                            </div>
                        </>
                        :
                        <>
                        </>
                    }
                >
                    {(() => {
                        const remaining = MAX_LABELS - labels.length
                        const limitReached = remaining <= 0
                        const nearLimit = !limitReached && remaining <= 10
                        const counterClass = limitReached
                            ? 'bg-red-50 text-red-700 ring-1 ring-red-200'
                            : nearLimit
                                ? 'bg-amber-50 text-amber-700 ring-1 ring-amber-200'
                                : 'bg-gray-50 text-gray-700 ring-1 ring-gray-200'
                        return (
                            <div className="mt-4 space-y-4">
                                <div className="flex items-end justify-between gap-3">
                                    <div>
                                        <p className="block text-sm font-medium text-gray-800">
                                            {i18n.t('reassign_carrier.scanner.label')}
                                        </p>
                                        <p className="mt-0.5 text-xs text-gray-500">
                                            {i18n.t('reassign_carrier.scanner.hint')}
                                        </p>
                                    </div>
                                    <span className={`text-xs font-medium tabular-nums rounded-md px-2.5 py-1 ${counterClass}`}>
                                        {i18n.t('reassign_carrier.scanner.counter', { count: labels.length, max: MAX_LABELS })}
                                    </span>
                                </div>

                                <div className="space-y-1">
                                    <label className="block text-sm font-medium text-gray-800">
                                        {i18n.t('reassign_carrier.batch_label_input')}
                                    </label>
                                    <input
                                        type="text"
                                        value={batchLabel}
                                        onChange={(e) => setBatchLabel(e.target.value)}
                                        placeholder={i18n.t('reassign_carrier.batch_label_placeholder')}
                                        maxLength={100}
                                        className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm font-mono shadow-sm placeholder-gray-400 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                                    />
                                    {labelLookup.state === 'idle' && (
                                        <p className="text-xs text-gray-400">{i18n.t('reassign_carrier.batch_label_hint_auto')}</p>
                                    )}
                                    {labelLookup.state === 'loading' && (
                                        <p className="text-xs text-gray-400 flex items-center gap-1">
                                            <span className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-gray-300 border-t-gray-600" />
                                        </p>
                                    )}
                                    {labelLookup.state === 'pending' && (
                                        <p className="text-xs text-green-700">
                                            {i18n.t('reassign_carrier.batch_label_hint_will_add', {
                                                label: labelLookup.data?.internal_label,
                                                count: labelLookup.data?.deliveries_count
                                            })}
                                        </p>
                                    )}
                                    {labelLookup.state === 'submitted' && (
                                        <p className="text-xs text-red-700">
                                            {i18n.t('reassign_carrier.batch_label_hint_submitted', {
                                                label: labelLookup.data?.internal_label
                                            })}
                                        </p>
                                    )}
                                    {labelLookup.state === 'available' && (
                                        <p className="text-xs text-gray-600">
                                            {i18n.t('reassign_carrier.batch_label_hint_will_create', {
                                                label: labelLookup.data?.internal_label
                                            })}
                                        </p>
                                    )}
                                    {labelLookup.state === 'error' && (
                                        <p className="text-xs text-red-700">
                                            {i18n.t('reassign_carrier.bulk_error_description')}
                                        </p>
                                    )}
                                </div>

                                <SearchInput
                                    ref={searchinputRef}
                                    onChange={handleSearchChange}
                                    debounce={500}
                                    placeholder={i18n.t('reassign_carrier.scanner.placeholder')}
                                />

                                {limitReached && (
                                    <div role="alert" className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
                                        <p className="font-medium">{i18n.t('reassign_carrier.scanner.limit_reached_title')}</p>
                                        <p className="mt-0.5 text-red-700">
                                            {i18n.t('reassign_carrier.scanner.limit_reached_advice', { max: MAX_LABELS })}
                                        </p>
                                    </div>
                                )}

                                {labels.length === 0 ? (
                                    <div className="rounded-md border border-dashed border-gray-200 px-4 py-8 text-center text-sm text-gray-500">
                                        {i18n.t('reassign_carrier.scanner.empty_state')}
                                    </div>
                                ) : (
                                    <ul className="divide-y divide-gray-100 rounded-md border border-gray-200 bg-white">
                                        {labels.map((label, idx) => (
                                            <li key={label} className="flex items-center justify-between gap-3 px-3 py-2 hover:bg-gray-50">
                                                <div className="flex items-center gap-3 min-w-0">
                                                    <span className="text-xs tabular-nums text-gray-400 w-6 text-right">
                                                        {labels.length - idx}
                                                    </span>
                                                    <span className="font-mono text-sm text-gray-900 truncate">
                                                        {label}
                                                    </span>
                                                </div>
                                                <button
                                                    type="button"
                                                    onClick={() => removeLabel(label)}
                                                    aria-label={i18n.t('reassign_carrier.scanner.remove_label', { label })}
                                                    className="rounded p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-200 transition-colors"
                                                >
                                                    <TrashIcon className="h-4 w-4" />
                                                </button>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                        )
                    })()}

                </CollapseContainer>
            </section>
            </>
        )
        }

        {showUberBatches && (
            <section className="mt-6">
                <CollapseContainer
                    options={{ collapse: false }}
                    headerComponent={
                        <h2 className="mb-0 text-2xl font-semi-bold">
                            {i18n.t('uber_batches.list_title')}
                        </h2>
                    }
                >
                    <UberBatchesList embedded />
                </CollapseContainer>
            </section>
        )}

      </PageView>

      <Notification
        show={!!notification}
        setShow={() => setNotification(null)}
        type={notification?.type || 'error'}
        title={notification?.title || ''}
        description={notification?.description || ''}
      />
      </>
    )
}
